import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getFromLocal,
  saveToLocal,
  removeFromLocal,
  clearLocalStorage,
} from "src/util/cache.service";
import { LOCAL_KEYS } from "src/util/util.constant";
import { resetAllStores } from "src/store/resetStore";
import ApiService from "src/util/api.service";
import { ServicesUrlEndpoints } from "src/services/servicesEndpointConstant";
import {
  hasSiblingTab,
  isSameTabResume,
  markTabSessionActive,
  clearTabSessionMarker,
} from "src/util/tabSession.service";

interface LoginContextValue {
  token: string | null;
  setToken: (token: string | null) => void;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  doLogout: () => Promise<void>;
  getToken: () => string | null;
}

export const LoginContext = createContext<LoginContextValue | null>(null);

interface LoginProviderProps {
  children: ReactNode;
}

export const LoginProvider = ({ children }: LoginProviderProps) => {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(
    () => getFromLocal<string>(LOCAL_KEYS.TOKEN, false, true)
  );
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => Boolean(getFromLocal(LOCAL_KEYS.IS_AUTHENTICATED, false, true))
  );
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  useEffect(() => {
    if (token) {
      saveToLocal(LOCAL_KEYS.TOKEN, token, false, true);
    } else {
      setIsAuthenticating(false);
    }
  }, [token]);

  // Only a token that was already in localStorage when this tab booted needs
  // the sibling-tab check below -- a token that arrives afterwards (an
  // interactive login, a token from a URL handoff, a cross-tab refresh) is
  // always trusted immediately, since it's necessarily happening in a live,
  // currently-open tab.
  const isBootRef = useRef(true);

  useEffect(() => {
    const isBoot = isBootRef.current;
    isBootRef.current = false;

    if (!token) return;

    const trustSession = () => {
      markTabSessionActive();
      setIsAuthenticated(true);
      setIsAuthenticating(false);
    };

    if (!isBoot) {
      trustSession();
      return;
    }

    let cancelled = false;

    const validateSession = async () => {
      // "Remember me" was checked at login, or this tab already validated
      // itself (e.g. a page refresh) -> no need to check for sibling tabs.
      const rememberMe = Boolean(
        getFromLocal<boolean>(LOCAL_KEYS.REMEMBER_ME, true, true)
      );
      const sessionStillValid =
        rememberMe || isSameTabResume() || (await hasSiblingTab());

      if (cancelled) return;

      if (!sessionStillValid) {
        // "Remember me" was unchecked and no other tab is keeping the
        // session alive -> the browser was fully closed, so log out.
        void doLogout();
        return;
      }

      trustSession();
    };

    validateSession();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const doLogout = async () => {
    try {
      await ApiService.post(ServicesUrlEndpoints.AUTH.LOGOUT);
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      setIsAuthenticated(false);
      resetAllStores();

      removeFromLocal(LOCAL_KEYS.TOKEN, true);
      removeFromLocal(LOCAL_KEYS.REFRESH_TOKEN, true);
      removeFromLocal(LOCAL_KEYS.IS_AUTHENTICATED, true);
      removeFromLocal(LOCAL_KEYS.USER_DETAIL, true);
      removeFromLocal(LOCAL_KEYS.ROLE, true);

      setToken(null);
      setIsAuthenticating(false);
      clearLocalStorage();
      queryClient.clear();
      clearTabSessionMarker();
    }
  };

  const getToken = () => token;

  const value = useMemo<LoginContextValue>(
    () => ({
      token,
      setToken,
      isAuthenticated,
      isAuthenticating,
      doLogout,
      getToken,
    }),
    [token, isAuthenticated, isAuthenticating]
  );

  return (
    <LoginContext.Provider value={value}>
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginContext = () => {
  const ctx = useContext(LoginContext);
  if (!ctx) throw new Error("useLoginContext must be used within LoginProvider");
  return ctx;
};
